import * as analytics from 'analytics/Analytics';
import {PROCEDURES_ANALYTICS_ACTIONS} from 'constants/analytics-products';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';

import {FilteredProcedureMethodSection} from './design/ProcedureMethodSectionFilterDropdown';
import {
  ProcedureValidationError,
  ProcedureValidationErrorType,
} from './design/validation/ProcedureValidationTypes';

export function trackToggledProcedureViewEvent(
  procedureId: string,
  procedureType: ProcedureType,
  actionOrigin: ActionOrigin.PROCEDURE_DESIGNER_FLOWCHART | ActionOrigin.PROCEDURE_DESIGNER_DETAILS
): void {
  analytics.trackEvent(PROCEDURES_ANALYTICS_ACTIONS.TOGGLED_PROCEDURE_VIEW, {
    actionOrigin,
    actionInstanceId: analytics.makeAnalyticsEventId(),
    eventGroupId: analytics.makeAnalyticsEventId(),
    procedureId,
    procedureType: procedureType.toLowerCase(),
  });
}

export function trackEncounteredConfigurationValidationError(
  procedureId: string,
  procedureType: ProcedureType,
  newValidationErrors: Array<ProcedureValidationError>
): void {
  for (const validationError of newValidationErrors) {
    analytics.trackEvent(PROCEDURES_ANALYTICS_ACTIONS.ENCOUNTERED_CONFIGURATION_VALIDATION_ERROR, {
      actionOrigin: ActionOrigin.PROCEDURE_DESIGNER,
      actionInstanceId: analytics.makeAnalyticsEventId(),
      eventGroupId: analytics.makeAnalyticsEventId(),
      procedureId,
      procedureType: procedureType.toLowerCase(),
      objectId: validationError.primaryObject.id,
      parentObjectIds: validationError.parentObjects.flatMap((parent) => parent.id),
      validationError: validationError.errorMessage,
      validationErrorType: ProcedureValidationErrorType[validationError.type],
    });
  }
}

export function trackDuplicatedProcedureEvent(
  sourceProcedureId: string,
  procedureId: string,
  procedureType: ProcedureType,
  templateCollectionId: string
): void {
  analytics.trackEvent(PROCEDURES_ANALYTICS_ACTIONS.DUPLICATED_PROCEDURE, {
    actionOrigin: ActionOrigin.PROCEDURE_DESIGNER,
    actionInstanceId: analytics.makeAnalyticsEventId(),
    eventGroupId: analytics.makeAnalyticsEventId(),
    sourceProcedureId,
    procedureId,
    templateCollectionId,
    procedureType: procedureType.toLowerCase(),
  });
}

export function trackMovedProcedureEvent(
  procedureId: string,
  procedureType: ProcedureType,
  previousTemplateCollectionId: string,
  templateCollectionId: string
): void {
  analytics.trackEvent(PROCEDURES_ANALYTICS_ACTIONS.MOVED_PROCEDURE, {
    actionOrigin: ActionOrigin.PROCEDURE_DESIGNER,
    actionInstanceId: analytics.makeAnalyticsEventId(),
    eventGroupId: analytics.makeAnalyticsEventId(),
    procedureId,
    previousTemplateCollectionId,
    templateCollectionId,
    procedureType: procedureType.toLowerCase(),
  });
}

export function trackFilteredProcedureFlowchartView(
  procedureId: string,
  procedureType: ProcedureType,
  selectedOption: FilteredProcedureMethodSection,
  filteredSections: Array<FilteredProcedureMethodSection>
): void {
  analytics.trackEvent(PROCEDURES_ANALYTICS_ACTIONS.FILTERED_PROCEDURE_FLOWCHART_VIEW, {
    actionOrigin: ActionOrigin.PROCEDURE_DESIGNER_FLOWCHART,
    actionInstanceId: analytics.makeAnalyticsEventId(),
    eventGroupId: analytics.makeAnalyticsEventId(),
    procedureId,
    selectedOption,
    filteredSections,
    procedureType: procedureType.toLowerCase(),
  });
}

export function trackSwitchedMethodConfiguration(
  procedureId: string,
  procedureType: ProcedureType,
  methodId: string | null,
  methodNodeId: string,
  methodCid: string
): void {
  analytics.trackEvent(PROCEDURES_ANALYTICS_ACTIONS.SWITCHED_METHOD_CONFIGURATION, {
    actionOrigin: ActionOrigin.PROCEDURE_DESIGNER_DETAILS,
    actionInstanceId: analytics.makeAnalyticsEventId(),
    eventGroupId: analytics.makeAnalyticsEventId(),
    procedureId,
    methodId,
    methodNodeId,
    methodCid,
    procedureType: procedureType.toLowerCase(),
  });
}

export function trackReorderedProcedureStepEvent(
  procedureId: string,
  procedureType: ProcedureType,
  methodNodeId: string,
  methodId: string | null,
  methodCid: string,
  stepId: string | null,
  stepCid: string,
  stepGroupType: string,
  direction: 'up' | 'down' | 'drag'
): void {
  analytics.trackEvent(PROCEDURES_ANALYTICS_ACTIONS.REORDERED_PROCEDURE_STEP, {
    actionOrigin: ActionOrigin.PROCEDURE_DESIGNER_DETAILS,
    actionInstanceId: analytics.makeAnalyticsEventId(),
    eventGroupId: analytics.makeAnalyticsEventId(),
    procedureId,
    methodNodeId,
    methodId,
    methodCid,
    stepId,
    stepCid,
    stepGroupType,
    direction,
    procedureType: procedureType.toLowerCase(),
  });
}
